import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { SearchResult } from '../../hooks/useDebouncedSearch'
import searchApi from '../../client/search-api'
import LargeCard from '../../components/cards/large-card'
import ContentContainer from '../../areas/layout/content-container'

const Wrapper = styled(Box)({
  marginTop: 32,
})

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 26,
  fontWeight: 600,
  marginBottom: 24,
  color: theme.palette.text.primary,
}))

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: 32,
}))

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 24,
})

const LoadMoreWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 32,
})

const LoadMoreButton = styled(Button)(({ theme }) => ({
  paddingLeft: 32,
  paddingRight: 32,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 8,
}))

const LoadingText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  marginTop: 24,
}))

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams()

  const query = searchParams.get('q') || ''
  const [page, setPage] = useState(1)

  const [results, setResults] = useState<SearchResult[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [initialLoaded, setInitialLoaded] = useState(false)

  const resetSearch = () => {
    setResults([])
    setPage(1)
    setInitialLoaded(false)
  }

  useEffect(() => {
    if (!query) return
    resetSearch()
  }, [query])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!query || initialLoaded) return
    fetchResults(1)
  }, [query, initialLoaded])
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchResults = async (pageToGet: number) => {
    setLoading(true)

    try {
      const res = await searchApi.search(query, pageToGet)
      const newResults = res.data.results || []

      if (pageToGet === 1) {
        setResults(newResults)
      } else {
        setResults((prev) => [...prev, ...newResults])
      }

      setTotalPages(res.data.totalPages)
    } finally {
      setLoading(false)
      setInitialLoaded(true)
    }
  }

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchResults(nextPage)
  }

  return (
    <ContentContainer>
      <Wrapper>
        <Title>Results for "{query}"</Title>

        {!loading && results.length === 0 && (
          <EmptyText>No results found</EmptyText>
        )}

        <Grid>
          {results.map((item) => (
            <LargeCard
              key={`${item.type}-${item.id}`}
              id={item.id}
              type={item.type}
              title={item.title}
              year={item.year || ''}
              posterUrl={item.posterUrl ?? undefined}
            />
          ))}
        </Grid>

        {!loading && page < totalPages && results.length > 0 && (
          <LoadMoreWrapper>
            <LoadMoreButton variant='outlined' onClick={loadMore}>
              Load More
            </LoadMoreButton>
          </LoadMoreWrapper>
        )}

        {loading && results.length > 0 && <LoadingText>Loading...</LoadingText>}
      </Wrapper>
    </ContentContainer>
  )
}
